'use client'

import type { CSSProperties, Ref } from 'react'
import { useEffect, useState } from 'react'
import type { SiteBannerBackgroundColor, SiteBannerResponse } from '@/app/api/site-banner/route'
import { useMarqueeCycleCopies } from '@/components/ui/use-marquee-cycle-copies'
import { cn } from '@/lib/utils'

type ActiveBanner = NonNullable<SiteBannerResponse['banner']>

const SECONDS_PER_COPY = 12
const SEPARATOR_GAP = '\u00A0\u00A0'

const backgroundColorMap: Record<SiteBannerBackgroundColor, string> = {
  primary: 'bg-background-primary',
  secondary: 'bg-background-secondary',
  tertiary: 'bg-background-tertiary',
}

function BannerTrack({
  text,
  separator,
  copies,
  trackRef,
}: {
  text: string
  separator: string | null
  copies: number
  trackRef?: Ref<HTMLDivElement>
}) {
  const divider = separator
    ? `${SEPARATOR_GAP}${separator}${SEPARATOR_GAP}`
    : `${SEPARATOR_GAP}${SEPARATOR_GAP}`

  return (
    <div ref={trackRef} className="flex shrink-0 items-center" aria-hidden="true">
      {Array.from({ length: copies }).map((_, index) => (
        <span
          key={index}
          className="whitespace-nowrap px-2 font-mono text-xs uppercase tracking-wide md:px-4 md:text-sm"
        >
          {text}
          <span aria-hidden="true">{divider}</span>
        </span>
      ))}
    </div>
  )
}

function BannerMarquee({
  text,
  separator,
  linkUrl,
  linkOpenInNewTab,
}: {
  text: string
  separator: string | null
  linkUrl: string | null
  linkOpenInNewTab: boolean
}) {
  const { containerRef, cycleCopies, cycleRef } = useMarqueeCycleCopies({ itemCount: 1 })
  const duration = `${cycleCopies * SECONDS_PER_COPY}s`

  const marquee = (
    <div ref={containerRef} className="flex flex-1 overflow-hidden">
      <div
        className="flex w-max animate-image-carousel-marquee motion-reduce:animate-none"
        style={
          {
            '--image-carousel-marquee-duration': duration,
          } as CSSProperties
        }
      >
        {[0, 1].map((groupIndex) => (
          <BannerTrack
            key={groupIndex}
            text={text}
            separator={separator}
            copies={cycleCopies}
            trackRef={groupIndex === 0 ? cycleRef : undefined}
          />
        ))}
      </div>
    </div>
  )

  if (!linkUrl) return marquee

  return (
    <a
      href={linkUrl}
      aria-label={text}
      className="flex flex-1 overflow-hidden transition-opacity hover:opacity-80"
      {...(linkOpenInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {marquee}
    </a>
  )
}

export function SiteBanner() {
  const [banner, setBanner] = useState<ActiveBanner | null>(null)

  useEffect(() => {
    const controller = new AbortController()

    async function loadBanner() {
      try {
        const response = await fetch('/api/site-banner', {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
        })
        if (!response.ok) return
        const data = (await response.json()) as SiteBannerResponse
        if (!controller.signal.aborted) {
          setBanner(data.banner)
        }
      } catch {
        // Swallow errors (including aborts); the banner is non-critical chrome.
      }
    }

    void loadBanner()

    return () => {
      controller.abort()
    }
  }, [])

  if (!banner) return null

  const showButton = banner.showButton && banner.buttonLabel && banner.buttonUrl
  const buttonOnRight = banner.buttonPosition === 'right'

  const button = showButton ? (
    <a
      href={banner.buttonUrl ?? '#'}
      className={cn(
        'flex shrink-0 items-center whitespace-nowrap border-theme-surface-border px-3 font-mono text-xs font-bold uppercase tracking-wide transition-colors hover:bg-theme-button-primary-background hover:text-theme-button-primary-foreground md:px-5 md:text-sm',
        buttonOnRight
          ? 'border-l [border-left-width:var(--header-border-width)]'
          : 'border-r [border-right-width:var(--header-border-width)]',
      )}
    >
      {banner.buttonLabel}
    </a>
  ) : null

  return (
    <div
      className={cn(
        'flex h-10 w-full shrink-0 items-stretch overflow-hidden border-b [border-bottom-width:var(--header-border-width)] border-theme-surface-border',
        backgroundColorMap[banner.backgroundColor] ?? backgroundColorMap.primary,
      )}
    >
      {!buttonOnRight && button}
      <BannerMarquee
        text={banner.text}
        separator={banner.separator}
        linkUrl={banner.linkUrl}
        linkOpenInNewTab={banner.linkOpenInNewTab}
      />
      {buttonOnRight && button}
    </div>
  )
}
